/* eslint-disable */
import { withStyles } from '@material-ui/core';
import List from '@material-ui/core/List';
import Snackbar from '@material-ui/core/Snackbar';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import withServices from '../../Contexts/Services/WithServices.context';
import getFilters from '../../State/Store/Filters/Selectors/Filters.selector';
import ordersActionCreators from '../../State/Store/Orders/Orders.actions';
import ordersErrorSelector from '../../State/Store/Orders/Selectors/OrdersError.selector';
import ordersLoadingSelector from '../../State/Store/Orders/Selectors/OrdersLoading.selector';
import sortedOrdersByDateRangeSelector from '../../State/Store/Orders/Selectors/OrdersSortedByDateRange.selector';
import isIOS from '../../Support/Is/IOS/IsIOS.support';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import Footer from '../Footer/Footer.component';
import OrderGroup from '../OrderGroup/OrderGroup.component';
import OrderListPropTypes from './OrderList.component.props';
import orderListStyles from './OrderList.component.styles';

/**
 * OrderListComponent
 * @export
 * @class OrderListComponent
 * @extends {React.Component<OrderListComponentComposedProps, OrderListComponentState>}
 */
export class OrderListComponent extends React.Component {
  preserveScrollRef = React.createRef();

  /**
   * The `props` to seed when any specific prop receives an `undefined` value. NOT a `null` value
   * @static
   * @type {Partial<OrderListComponentProps>}
   * @memberof OrderListComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`OrderList`}
   * @memberof OrderListComponent
   */
  static displayName = `OrderList`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof OrderListComponent
   */
  static propTypes = OrderListPropTypes;

  /**
   * Data specific to the `<OrderListComponent />` instance
   * @type {OrderListComponentState}
   * @memberof OrderListComponent
   */
  state = {
    controlShowError: false,
    showError: this.props.ordersError /* eslint-disable-line react/destructuring-assignment */,
  };

  /**
   * isOrderIdInUrl
   * @returns {boolean} if order id is in url hash
   * @memberof OrderCardComponent
   */
  isOrderIdInUrl = () => {
    const { orderId } = this.props;
    return window.location.hash === `#${orderId}`;
  };

  /**
   * componentDidMount
   * @returns {void} void
   * @memberof OrderListComponent
   */
  componentDidMount() {
    if (isIOS()) {
      window.scroll(10, 10);
    }
    this.requestOrdersSummary();
  }

  /**
   * componentDidUpdate
   * @returns {void} void
   * @param {any} prevProps prevProps
   * @param {any} prevState prevState
   * @memberof OrderListComponent
   */
  componentDidUpdate(prevProps, prevState) {
    const {
      filters: { addresses: prevAddresses, ...prevFiltersProps },
    } = prevProps;
    const {
      filters: { addresses: thisAddresses, ...thisFiltersProps },
      filters,
    } = this.props;
    const hasAddressesChanged = !isEqual(prevAddresses, thisAddresses);
    const hasOtherFiltersChanged = !isEqual(prevFiltersProps, thisFiltersProps);
    const onlyAddessesChanged = hasAddressesChanged && !hasOtherFiltersChanged;
    if (onlyAddessesChanged) {
      return;
    }
    if (!isEqual(prevProps.filters, filters)) {
      this.requestOrdersSummary();
    }
    // scroll to card that is specified in url hash
    const { ordersLoading } = this.props;
    const card = window.document.getElementById(`orderCard-${window.location.hash.replace(`#`, ``)}`);
    if (prevProps.ordersLoading !== ordersLoading && !ordersLoading && card) {
      // we also delay scrolling down to make sure all devices got this right
      setTimeout(() => card.scrollIntoView({ block: `center`, behavior: `smooth` }), 1000);
    }
  }

  /**
   * handleClose
   * @returns {void} void
   * @memberof OrderListComponent
   */
  handleClose = () => {
    this.setState({
      controlShowError: true,
      showError: false,
    });
  };

  /**
   * renderError
   * @returns {React.Component} component
   * @param {string} ordersError ordersError
   * @memberof OrderListComponent
   */
  renderError(ordersError) {
    const { controlShowError, showError } = this.state;
    const message = `Service temporarily unavailable.`;
    return <Snackbar message={message} onClose={this.handleClose} open={controlShowError ? showError : !!ordersError} />;
  }

  /**
   * requestOrdersSummary
   * @returns {void} void
   * @memberof OrderListComponent
   */
  requestOrdersSummary = debounce(() => {
    const { services } = this.props;
    if (!services.authService.isAuthenticated()) {
      return;
    }
    const { actions } = this.props;
    actions.ordersAll();
  }, 700);

  /**
   * Render the component
   * @returns {React.Component} The rendered `<OrderListComponent />`
   * @memberof OrderListComponent
   */
  render() {
    const { classes, orders, ordersLoading, ordersError, filters } = this.props;
    return (
      <div ref={this.preserveScrollRef}>
        {ordersError && this.renderError(ordersError)}
        <List classes={{ dense: classes.spaceBetween, root: classes.root }} dense subheader={<li />}>
          <OrderGroup filters={filters} orderGroup={orders} ordersLoading={ordersLoading} />
          <Footer />
        </List>
      </div>
    );
  }
}

/** @type {React.ComponentClass<OrderListComponentProps>} */
const OrderList = compose(
  withServices,
  withStyles(themeCreateInjectableStyles(orderListStyles), { withTheme: true }),
  connect(
    (state) => ({
      filters: getFilters(state),
      orders: sortedOrdersByDateRangeSelector(state),
      ordersError: ordersErrorSelector(state),
      ordersLoading: ordersLoadingSelector(state),
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...ordersActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(OrderListComponent);

export default OrderList;
